import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper, Button } from '@material-ui/core';
import { useIntegrationWizard } from '../../wizard/IntegrationWizardContext';
import ConfigStep from './index';
import ResultsPageIntegrationStep from '../ResultsPageIntegrationStep';
import DropdownIntegrationStep from '../DropdownIntegrationStep';
import styles from './StepRouter.module.scss';
import IntlMessages from 'util/IntlMessages';

const STEP_COMPONENTS = {
  'searchbox-config': (props) => <ConfigStep {...props} view="searchBox" />,
  'dropdown-config': (props) => <ConfigStep {...props} view="dropdown" />,
  'classic-config': (props) => <ConfigStep {...props} view="dropdown" />,
  'resultspage-config': (props) => <ConfigStep {...props} view="resultsPage" />,
  'classic-integration': (props) => <DropdownIntegrationStep {...props} />,
  'dropdown-integration': (props) => <DropdownIntegrationStep {...props} />,
  'resultspage-integration': (props) => <ResultsPageIntegrationStep {...props} />,
};

const StepRouter = ({
  stepId,
  title,
  description,
  data,
}) => {
  const context = useIntegrationWizard();
  const { onUpdate, onReady } = context;
  const [mockCompleted, setMockCompleted] = React.useState(false);

  // Check if we have a specific component for this step.
  const StepComponent = STEP_COMPONENTS[stepId];
  const isPlaceholder = !StepComponent;

  useEffect(() => {
    // Only auto-ready the placeholder fallback.
    // Real step components manage their own onReady calls based on field validation.
    if (isPlaceholder && onReady) {
      onReady(true);
    }
  }, [onReady, isPlaceholder]);

  const handleMockAction = () => {
    setMockCompleted(true);
    if (onUpdate) {
      onUpdate({ [stepId]: { completed: true, timestamp: Date.now() } });
    }
  };

  if (StepComponent) {
    return (
      <Box className={styles.root}>
        <StepComponent
          stepId={stepId}
          title={title}
          description={description}
          data={data}
          {...context}
        />
      </Box>
    );
  }

  // Fallback to placeholder
  return (
    <Box className={styles.root}>
      <Paper className={styles.placeholder} elevation={0}>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            {description}
          </Typography>
        )}
        <Typography variant="body2" className={styles.stepId}>
          <IntlMessages id="ui.integrationWizard.embedded.configStep.stepRouter.stepId" />
          {' '}
          {stepId}
        </Typography>
        <Typography variant="body2" color="textSecondary" align="center">
          <IntlMessages id="ui.integrationWizard.embedded.configStep.stepRouter.configurationComponentWillBeImplementedHere" />
        </Typography>

        <Button
          variant="outlined"
          className={styles.mockButton}
          onClick={handleMockAction}
          color={mockCompleted ? 'primary' : 'default'}
        >
          {mockCompleted
            ? <IntlMessages id="ui.integrationWizard.embedded.configStep.stepRouter.configured" />
            : <IntlMessages id="ui.integrationWizard.embedded.configStep.stepRouter.mockConfigure" />}
        </Button>
      </Paper>
    </Box>
  );
};

StepRouter.propTypes = {
  stepId: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  data: PropTypes.shape({}),
};

StepRouter.defaultProps = {
  description: null,
  data: {},
};

export default StepRouter;
